import {Badge} from 'platform/components';
import {Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {SaveSaleVehicleFinancingBody, VehicleFinancingType} from '@dms/api/vehicle';
import {
  FinancialProductCalculator,
  type FinancialProductCalculatorSelection,
} from '@dms/features/financial-products';
import i18n from '@dms/i18n';

import {
  constrainFinancingSelection,
  getDownPaymentAdjustment,
  getFinancingControlLimits,
  type DownPaymentAdjustment,
  type FinancingControlDimensions,
} from 'features/financial-products';

import {VehicleFinancingPricing} from './types/VehicleFinancingPricing';
import {calculateFinancingOffer} from './utils/calculateFinancingOffer';
import {resolveFinancingRateCell} from './utils/financingCells/resolveFinancingRateCell';

interface FinancingCalculatorProps {
  vehicleFinancingBody: SaveSaleVehicleFinancingBody;
  type: VehicleFinancingType;
  balloonPayment: number | null;
  balloonPayments: number[];
  vehiclePrice: number;
  currency: string;
  pricing: VehicleFinancingPricing | null;
  downPaymentStep: number;
  onBalloonPaymentChange: (value: number | null) => void;
}

/** The vehicle editor prices the shared calculator locally against its draft matrix. */
export function FinancingCalculator(props: FinancingCalculatorProps) {
  const rateMatrix = props.vehicleFinancingBody.rateMatrix;
  // The draft cells drive the same per-term limits the business case and the online offer read
  // from `availableFinancingCells`; `availableTerms` (the enabled terms) drops cells on any other.
  const dimensions: FinancingControlDimensions = {
    availableTerms: rateMatrix.availableTerms,
    availableDownPaymentPercentages: rateMatrix.cells.map((cell) => cell.downPayment),
    downPaymentStep: props.downPaymentStep,
    availableBalloonPaymentPercentages: props.type === 'BALLOON' ? props.balloonPayments : [],
    availableFinancingCells: rateMatrix.cells.map((cell) => ({
      termMonths: cell.term,
      downPaymentPercentage: cell.downPayment,
      balloonPaymentPercentage: cell.balloonPayment,
    })),
  };
  const [selectedTerm, setSelectedTerm] = useState<number | null>(null);
  const [selectedDownPayment, setSelectedDownPayment] = useState<number | null>(null);
  const [adjustment, setAdjustment] = useState<DownPaymentAdjustment | null>(null);
  const availableTerms = getFinancingControlLimits(dimensions, {
    termMonths: null,
    downPaymentPercentage: null,
    balloonPaymentPercentage: null,
  }).terms;
  const term =
    [selectedTerm, props.vehicleFinancingBody.activeTerm].find(
      (candidate): candidate is number => candidate !== null && availableTerms.includes(candidate)
    ) ??
    availableTerms[0] ??
    null;
  const downPaymentLimits = getFinancingControlLimits(dimensions, {
    termMonths: term,
    downPaymentPercentage: null,
    balloonPaymentPercentage: props.balloonPayment,
  }).downPayment;
  const downPayment =
    [selectedDownPayment, props.vehicleFinancingBody.activeDownPayment].find(
      (candidate): candidate is number =>
        candidate !== null &&
        isNotNil(downPaymentLimits) &&
        candidate >= downPaymentLimits.min &&
        candidate <= downPaymentLimits.max
    ) ??
    downPaymentLimits?.min ??
    null;
  const limits = getFinancingControlLimits(dimensions, {
    termMonths: term,
    downPaymentPercentage: downPayment,
    balloonPaymentPercentage: props.balloonPayment,
  });
  const cell =
    term === null || downPayment === null
      ? null
      : resolveFinancingRateCell({
          cells: rateMatrix.cells,
          term,
          downPayment,
          balloonPayment: props.type === 'BALLOON' ? props.balloonPayment : null,
        });
  const calculation =
    isNotNil(cell) && term !== null && downPayment !== null
      ? calculateFinancingOffer({
          pricing: props.pricing,
          term,
          downPayment,
          balloonPayment: props.type === 'BALLOON' ? props.balloonPayment : null,
          nominalRate: cell.nominalRate,
          insurance: {
            included: props.vehicleFinancingBody.insuranceIncludedInCalculation,
            liabilityMonthly: props.vehicleFinancingBody.liabilityInsuranceMonthly,
            comprehensiveMonthly: props.vehicleFinancingBody.comprehensiveInsuranceMonthly,
          },
        })
      : null;
  const isDefault =
    props.vehicleFinancingBody.activeTerm === term &&
    props.vehicleFinancingBody.activeDownPayment === downPayment &&
    props.vehicleFinancingBody.activeBalloonPayment === props.balloonPayment;
  const downPaymentSliderValue = downPayment ?? 0;

  const handleSelectionChange = (selection: FinancialProductCalculatorSelection) => {
    // A term or balloon change moves the down payment up to the new lowest bracket at once, so the
    // calculator never sits below the range it shows.
    const constrained = constrainFinancingSelection(dimensions, selection);

    setAdjustment(
      selection.termMonths !== term || selection.balloonPaymentPercentage !== props.balloonPayment
        ? getDownPaymentAdjustment(selection, constrained)
        : null
    );
    setSelectedTerm(constrained.termMonths);
    setSelectedDownPayment(constrained.downPaymentPercentage);

    if (constrained.balloonPaymentPercentage !== props.balloonPayment) {
      props.onBalloonPaymentChange(constrained.balloonPaymentPercentage);
    }
  };

  return (
    <FinancialProductCalculator
      selection={{
        termMonths: term,
        downPaymentPercentage: downPaymentSliderValue,
        balloonPaymentPercentage: props.balloonPayment,
      }}
      onSelectionChange={handleSelectionChange}
      availableTerms={limits.terms}
      availableDownPaymentPercentages={limits.downPayment?.ticks ?? []}
      downPaymentBrackets={limits.downPayment?.brackets}
      downPaymentAdjustment={adjustment}
      downPaymentStep={props.downPaymentStep}
      availableBalloonPaymentPercentages={
        props.type === 'BALLOON' ? limits.balloonPaymentPercentages : []
      }
      vehiclePrice={props.vehiclePrice}
      currency={props.currency}
      result={
        isNotNil(calculation) && isNotNil(cell)
          ? {
              monthlyPayment: calculation.monthlyRate,
              nominalRate: cell.nominalRate,
              apr: cell.apr,
              netCredit: calculation.netCredit,
              grossCredit: calculation.grossCredit,
              totalInterest: calculation.totalInterest,
              totalPayable: calculation.totalAmount,
            }
          : null
      }
      badge={
        // eslint-disable-next-line eag/prefer-show -- Pre-existing violation
        isDefault ? (
          <Badge
            value={i18n.t('page.vehicle.financingAndLeasing.defaultOption')}
            colorScheme="yellow"
            variant="subtle"
          />
        ) : null
      }
      emptyResult={
        <VStack spacing={2}>
          <Badge
            value={i18n.t('page.vehicle.financingAndLeasing.outsideMatrix')}
            colorScheme="orange"
            variant="subtle"
          />
          <Text color="tertiary">
            {i18n.t('page.vehicle.financingAndLeasing.outsideMatrixExplanation')}
          </Text>
        </VStack>
      }
      data-testid="financing-calculator"
    />
  );
}
